import { KeyboardEvent, MouseEvent, useCallback, useMemo } from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  DateYMDString,
  ICoursesCalendarSlotDto,
  normalizeCoursesCalendarSelectedSlotIds,
  selectedCoursesCalendarSlotIdsAtom,
  SelectionEnum,
  toCoursesCalendarCellId,
  useDrawers,
  useHaveCustomPermissions,
  wholeRowSelectedCoursesCalendarSF,
} from "@ryde/shared";

import { coursesCalendarSlotKey } from "./courses-calendar-cell.utils";

export interface IUseCoursesCalendarCellParams {
  dateKey: DateYMDString;
  hourIndex: number;
  slotMap: Map<string, ICoursesCalendarSlotDto[]>;
  onCreateSlotRide: (slot: ICoursesCalendarSlotDto) => Promise<number>;
  onAfterSlotProduce?: () => void;
}

export const useCoursesCalendarCell = ({
  dateKey,
  hourIndex,
  slotMap,
  onCreateSlotRide,
  onAfterSlotProduce,
}: IUseCoursesCalendarCellParams) => {
  const { hasPermissionEditCourse } = useHaveCustomPermissions();
  const {
    openCourseEditFormDrawer,
    openRideEditFormDrawer,
    openCoursesCalendarRideProduceDrawer,
  } = useDrawers();

  const list = useMemo(
    () => slotMap.get(coursesCalendarSlotKey(dateKey, hourIndex)) ?? [],
    [dateKey, hourIndex, slotMap],
  );

  const cellId = useMemo(
    () => toCoursesCalendarCellId(dateKey, hourIndex),
    [dateKey, hourIndex],
  );

  const cellSlotIds = useMemo(() => list.map(slot => slot.id), [list]);

  const [selectedSlotIdsRaw, setSelectedSlotIds] = useRecoilState(
    selectedCoursesCalendarSlotIdsAtom,
  );
  const selectedSlotIds =
    normalizeCoursesCalendarSelectedSlotIds(selectedSlotIdsRaw);

  const selectedInCellCount = useMemo(
    () => list.filter(slot => selectedSlotIds.has(slot.id)).length,
    [list, selectedSlotIds],
  );

  const isAllInCellSelected =
    list.length > 0 && selectedInCellCount === list.length;
  const hasSlotSelectionInCell = selectedInCellCount > 0;

  const hourRowSelectState = useRecoilValue(
    wholeRowSelectedCoursesCalendarSF(hourIndex),
  );
  const isFullHourRowSelected = hourRowSelectState === SelectionEnum.All;

  const isSlotSelected = useCallback(
    (slotId: string): boolean => selectedSlotIds.has(slotId),
    [selectedSlotIds],
  );

  const setSlotSelected = (slotId: string, isSelect: boolean): void => {
    setSelectedSlotIds(prev => {
      const next = new Set(normalizeCoursesCalendarSelectedSlotIds(prev));
      if (isSelect) {
        next.add(slotId);
      } else {
        next.delete(slotId);
      }
      return next;
    });
  };

  const toggleSlotSelected = (slotId: string): void => {
    setSlotSelected(slotId, !selectedSlotIds.has(slotId));
  };

  const setAllInCellSelected = (isSelect: boolean): void => {
    setSelectedSlotIds(prev => {
      const next = new Set(normalizeCoursesCalendarSelectedSlotIds(prev));
      for (const slotId of cellSlotIds) {
        if (isSelect) {
          next.add(slotId);
        } else {
          next.delete(slotId);
        }
      }
      return next;
    });
  };

  const openRideEdit = (id: number): void => {
    void openRideEditFormDrawer({ id });
  };

  const onOpenCourseClickHandler = (
    e: MouseEvent | KeyboardEvent,
    courseId: number,
  ): void => {
    if (!hasPermissionEditCourse) {
      return;
    }
    e.stopPropagation();
    void openCourseEditFormDrawer({ id: courseId });
  };

  const onCreateRideClickHandler = (
    e: MouseEvent,
    slot: ICoursesCalendarSlotDto,
  ): void => {
    e.stopPropagation();
    void openCoursesCalendarRideProduceDrawer({
      slots: [slot],
      createSlotRide: onCreateSlotRide,
      shouldUseSharedProduceFlow: true,
      onAfterChange: onAfterSlotProduce,
    });
  };

  const courseOpenClassName = hasPermissionEditCourse
    ? "cc-course-slot--editable"
    : undefined;

  return {
    cellId,
    list,
    selectedInCellCount,
    isAllInCellSelected,
    hasSlotSelectionInCell,
    isFullHourRowSelected,
    isSlotSelected,
    toggleSlotSelected,
    setAllInCellSelected,
    courseOpenClassName,
    onOpenCourseClickHandler,
    onCreateRideClickHandler,
    openRideEdit,
  };
};
